import type { ReactNode } from 'react'

interface Props {
  loading: boolean
  error?: string
  /** True when the query succeeded and returned nothing. */
  empty?: boolean
  emptyMessage?: string
  /** Reserved while there is nothing to draw, so the page does not jump. */
  height: number
  children: ReactNode
}

/** The four states a chart can be in, rendered so they cannot be confused.
 *
 *  Chiefly: while a query is in flight there is **no chart**. Leaving the
 *  previous one drawn is the conventional way to avoid a flicker, and here it
 *  means a reader can be looking at Lajanuri's year under a heading that says
 *  Shaori, with only a small "loading" line to tell them otherwise. Every
 *  other care taken in this application — station-local timestamps, stored
 *  versus derived, no sampling — is undone by a chart that is quietly of
 *  something else.
 *
 *  The space is held open rather than collapsed. A panel that vanishes and
 *  returns makes the page jump under the cursor, which is its own way of
 *  losing someone's place.
 */
export function ChartFrame({ loading, error, empty, emptyMessage, height,
                             children }: Props) {
  if (loading) {
    return (
      <div className="chart-placeholder" style={{ height }}>
        <span className="muted">Loading…</span>
      </div>
    )
  }
  if (error) return <p className="error">{error}</p>
  if (empty) {
    return (
      <div className="chart-placeholder" style={{ height }}>
        <span className="muted">{emptyMessage ?? 'No data in this range.'}</span>
      </div>
    )
  }
  return <>{children}</>
}
